import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

function errorMessage(error) {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : detail?.message || 'Unable to complete the request. Please try again.';
}

export default function SharePointLibraryDialog({ connector, onClose }) {
  const [files, setFiles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [audience, setAudience] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    Promise.all([
      api.get(`/connectors/${connector.id}/source-documents`),
      api.get('/groups'),
    ]).then(([source, access]) => {
      if (active) {
        setFiles(source.data.documents);
        setGroups(access.data);
      }
    }).catch(e => { if (active) setError(errorMessage(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [connector.id, revision]);

  async function importFile(file) {
    setPending(file.id);
    setError('');
    setResult('');
    try {
      const { data } = await api.post(`/connectors/${connector.id}/import`, {
        item_id: file.id, etag: file.etag, group_ids: [audience], acknowledge_copy: acknowledged,
      });
      setFiles(items => items.map(item => item.id === file.id ? { ...item, document_id: data.id } : item));
      setResult(data.already_imported
        ? `${file.name}: this source version was already imported. Its Eva audience and status are unchanged.`
        : `${file.name} was imported as a draft. Review extraction, content and audience in Knowledge Base before publication.`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPending(null);
    }
  }

  return (
    <Dialog open onOpenChange={open => { if (!open && !pending) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{connector.name}: import from SharePoint</DialogTitle>
          <DialogDescription>
            Imported copies use Eva access controls. SharePoint permissions, changes and deletions are not mirrored.
            Each new source version creates a separate draft; existing Eva documents stay unchanged.
          </DialogDescription>
        </DialogHeader>
        <label className="space-y-1 text-sm">
          <span>Eva audience after publication</span>
          <select className="block w-full border rounded p-2 bg-background" value={audience}
            disabled={!!pending || loading} onChange={e => setAudience(e.target.value)}>
            <option value="">Choose an audience</option>
            <option value="all">All Employees</option>
            {groups.filter(group => group.slug !== 'all-employees').map(group => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </label>
        <label className="flex gap-2 items-start text-sm">
          <input type="checkbox" checked={acknowledged} disabled={!!pending}
            onChange={e => setAcknowledged(e.target.checked)} />
          <span>I understand that Eva manages access to these copies independently of SharePoint.</span>
        </label>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {result && <p role="status" className="text-sm">{result} <a href="/kb/content" className="underline">Open Knowledge Base</a></p>}
        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">PDF and enabled XLSX or DOCX files, up to 40 MB each.</p>
          <Button variant="outline" disabled={loading || !!pending} onClick={() => setRevision(n => n + 1)}>Refresh library</Button>
        </div>
        {loading ? <p role="status">Loading SharePoint library...</p> : !error && files.length === 0 ? <p>No files found in this library.</p> : null}
        {!loading && <ul className="divide-y">
          {files.map(file => (
            <li key={file.id} className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium break-words">{file.name}</p>
                {file.source_url && <a href={file.source_url} target="_blank" rel="noopener noreferrer" className="text-sm underline">View in SharePoint</a>}
                {!file.supported && <p className="text-sm text-muted-foreground">Unsupported format, disabled format, or over 40 MB.</p>}
                {file.document_id && <p className="text-sm">This version is already imported.</p>}
              </div>
              <Button disabled={!!pending || !audience || !acknowledged || !file.supported || !file.etag || !!file.document_id}
                onClick={() => importFile(file)}>
                {pending === file.id ? 'Importing...' : 'Import draft'}
              </Button>
            </li>
          ))}
        </ul>}
      </DialogContent>
    </Dialog>
  );
}
